import React from "react";
import { downloadAccountingProjectSummaryPdf } from "../../../api/accounting";

export const PROJECT_ACCOUNTING_PRINT_STYLES = `
  @media print {
    .project-accounting-no-print { display: none !important; }
    .project-accounting-print-root {
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
      max-width: none !important;
    }
    .project-accounting-print-root .shadow,
    .project-accounting-print-root .shadow-sm,
    .project-accounting-print-root .shadow-lg,
    .project-accounting-print-root .rounded-2xl {
      box-shadow: none !important;
    }
    .project-accounting-print-appendix {
      break-before: page;
      page-break-before: always;
    }
    nav, aside, header[class*="sidebar"], [class*="Sidebar"] {
      display: none !important;
    }
  }
`;

export const formatAccountingCurrency = (value) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

export const formatAccountingPercentage = (value) =>
  `${(parseFloat(value) || 0).toFixed(1)}%`;

const contractSumSourceKey = (source) =>
  ({
    contract_lines: "accounting.project_summary.contract_sum_source_lines",
    contract_agreement: "accounting.project_summary.contract_sum_source_agreement",
    contract_lines_and_agreement: "accounting.project_summary.contract_sum_source_both",
    none: "accounting.project_summary.contract_sum_source_none",
  }[source] || "accounting.project_summary.contract_sum_source_none");

const laborSourceKey = (source) =>
  source === "mixed" ? "accounting.project_summary.labor_source_mixed" : source === "voucher"
    ? "accounting.project_summary.labor_source_voucher"
    : "accounting.project_summary.labor_source_hours";

const buildFallbackCalculationNotes = (summary) => {
  const labor = summary?.labor || {};
  const marginBasis = summary?.margin_basis || "contract";
  const source = summary?.contract_sum_source || "none";
  const contractSumKey = {
    contract_lines: "contract_sum_lines",
    contract_agreement: "contract_sum_agreement",
    contract_lines_and_agreement: "contract_sum_lines_primary",
    none: "contract_sum_none",
  }[source] || "contract_sum_none";

  const notes = [
    { section: "contract_sum", key: contractSumKey },
    { section: "income_invoiced", key: "income_invoiced" },
    { section: "income_expected", key: "income_expected" },
    {
      section: "labor",
      key: labor.source === "voucher" ? "labor_primary_voucher" : "labor_primary_hours",
    },
    { section: "labor_hours", key: "labor_hours" },
    { section: "labor_voucher", key: "labor_voucher" },
    { section: "material_costs", key: "material_costs" },
    { section: "supplier_invoices", key: "supplier_invoices" },
    { section: "total_costs", key: "total_costs" },
    {
      section: "profit",
      key: marginBasis === "contract" ? "profit_contract" : "profit_invoiced",
    },
    {
      section: "margin",
      key: marginBasis === "contract" ? "margin_contract" : "margin_invoiced",
    },
  ];

  if ((summary?.contract_agreement_price || 0) > 0) {
    notes.splice(1, 0, { section: "contract_agreement_price", key: "contract_agreement_price" });
  }
  if ((summary?.voucher_expenses || 0) > 0) {
    notes.splice(notes.length - 3, 0, { section: "voucher_expenses", key: "voucher_expenses" });
  }

  return notes;
};

const MethodologyAppendix = ({ summary, t, className = "" }) => {
  const notes = summary?.calculation_notes?.length
    ? summary.calculation_notes
    : buildFallbackCalculationNotes(summary);

  const sectionLabel = (section) => {
    const labels = {
      contract_sum: t("accounting.project_summary.contract_sum"),
      contract_agreement_price: t("accounting.project_summary.contract_agreement_price"),
      income_invoiced: t("accounting.project_summary.invoiced"),
      income_expected: t("accounting.project_summary.expected_module_income"),
      labor: t("accounting.project_summary.labor_costs"),
      labor_hours: t("accounting.project_summary.labor_hours_estimate"),
      labor_voucher: t("accounting.project_summary.labor_voucher"),
      material_costs: t("accounting.project_summary.material_costs"),
      supplier_invoices: t("accounting.project_summary.supplier_invoices"),
      voucher_expenses: t("accounting.project_summary.voucher_expenses"),
      total_costs: t("accounting.project_summary.total_costs"),
      profit: t("accounting.project_summary.profit"),
      margin: t("accounting.project_summary.margin"),
    };
    return labels[section] || section;
  };

  return (
    <section className={`project-accounting-print-appendix mt-8 border-t border-gray-300 pt-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {t("accounting.project_summary.print.methodology_title")}
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        {t("accounting.project_summary.print.methodology_intro")}
      </p>
      <dl className="space-y-3">
        {notes.map((note) => (
          <div key={`${note.section}-${note.key}`} className="text-sm">
            <dt className="font-medium text-gray-900">{sectionLabel(note.section)}</dt>
            <dd className="text-gray-600 mt-0.5">
              {t(`accounting.project_summary.methodology.${note.key}`)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export const ProjectAccountingPrintHeader = ({ summary, t }) => {
  const printedAt = new Date().toLocaleString("no-NO");
  const year = summary?.last_updated
    ? new Date(summary.last_updated).getFullYear()
    : new Date().getFullYear();

  return (
    <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-800">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
        {t("accounting.project_summary.print.document_title")}
      </p>
      <h1 className="text-2xl font-bold text-gray-900">{summary.project_name}</h1>
      <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-gray-700">
        {summary.project_number ? (
          <p>
            <span className="font-medium">{t("accounting.project_summary.print.project_number")}: </span>
            #{summary.project_number}
          </p>
        ) : null}
        {summary.customer_name ? (
          <p>
            <span className="font-medium">{t("accounting.project_summary.print.customer")}: </span>
            {summary.customer_name}
          </p>
        ) : null}
        <p>
          <span className="font-medium">{t("accounting.project_summary.print.year")}: </span>
          {year}
        </p>
        <p>
          <span className="font-medium">{t("accounting.project_summary.print.printed_at")}: </span>
          {printedAt}
        </p>
        <p>
          <span className="font-medium">{t("accounting.project_summary.last_updated")}: </span>
          {new Date(summary.last_updated).toLocaleString("no-NO")}
        </p>
      </div>
    </div>
  );
};

export const ProjectAccountingPrintContent = ({
  summary,
  t,
  showMethodologyOnScreen = false,
}) => {
  if (!summary) return null;

  const labor = summary.labor || {};
  const income = summary.income || {};
  const contractSumHint = t(contractSumSourceKey(summary.contract_sum_source));
  const laborHint = t(laborSourceKey(labor.source));
  const marginBasis = summary.margin_basis || "contract";

  const Row = ({ label, value, valueClass = "font-medium text-gray-900", hint = null }) => (
    <div className="flex justify-between gap-4 py-1.5">
      <span className="text-gray-600">{label}</span>
      <span className={`text-right ${valueClass}`}>{value}</span>
    </div>
  );

  return (
    <>
      <ProjectAccountingPrintHeader summary={summary} t={t} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 print:grid-cols-4 print:gap-3">
        {[
          { label: t("accounting.project_summary.contract_sum"), value: formatAccountingCurrency(summary.contract_sum) },
          { label: t("accounting.project_summary.invoiced"), value: formatAccountingCurrency(summary.invoiced_amount) },
          {
            label: t("accounting.project_summary.profit"),
            value: formatAccountingCurrency(summary.profit),
            valueClass: `font-medium ${summary.profit >= 0 ? "text-green-700" : "text-red-700"}`,
          },
          {
            label: t("accounting.project_summary.margin"),
            value: formatAccountingPercentage(summary.margin),
            valueClass: `font-medium ${summary.margin >= 0 ? "text-green-700" : "text-red-700"}`,
          },
        ].map((metric) => (
          <div key={metric.label} className="border border-gray-200 rounded-lg p-3 print:p-2">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{metric.label}</p>
            <p className={`text-lg font-semibold mt-1 ${metric.valueClass || ""}`}>{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2 print:gap-4">
        <section className="border border-gray-200 rounded-lg p-5 print:p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-3 border-b pb-2">
            {t("accounting.project_summary.income_section")}
          </h3>
          <div className="text-sm space-y-1">
            <Row label={t("accounting.project_summary.contract_sum")} value={formatAccountingCurrency(summary.contract_sum)} />
            <p className="text-xs text-gray-500 pb-1">{contractSumHint}</p>
            {summary.contract_agreement_price > 0 ? (
              <Row
                label={t("accounting.project_summary.contract_agreement_price")}
                value={formatAccountingCurrency(summary.contract_agreement_price)}
              />
            ) : null}
            <Row label={t("accounting.project_summary.invoiced")} value={formatAccountingCurrency(income.invoiced)} />
            <Row
              label={t("accounting.project_summary.expected_module_income")}
              value={formatAccountingCurrency(income.expected_from_modules)}
              valueClass="font-medium text-green-700"
            />
            <div className="border-t pt-2 mt-2">
              <Row
                label={t("accounting.project_summary.income_total")}
                value={formatAccountingCurrency(income.total_invoiced_and_expected)}
                valueClass="font-semibold text-gray-900"
              />
            </div>
          </div>
        </section>

        <section className="border border-gray-200 rounded-lg p-5 print:p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-3 border-b pb-2">
            {t("accounting.project_summary.costs_section")}
          </h3>
          <div className="text-sm space-y-1">
            <div className="bg-gray-50 rounded p-2 mb-2 print:bg-transparent print:border print:border-gray-200">
              <Row label={t("accounting.project_summary.labor_costs")} value={formatAccountingCurrency(labor.costs)} />
              <p className="text-xs text-blue-800 font-medium">{laborHint}</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                <span>
                  {t("accounting.project_summary.labor_hours_estimate")}: {formatAccountingCurrency(labor.hours_based)}
                </span>
                <span>
                  {t("accounting.project_summary.labor_voucher")}: {formatAccountingCurrency(labor.voucher_based)}
                </span>
              </div>
            </div>
            <Row label={t("accounting.project_summary.material_costs")} value={formatAccountingCurrency(summary.material_costs)} />
            <Row label={t("accounting.project_summary.supplier_invoices")} value={formatAccountingCurrency(summary.supplier_invoices)} />
            {summary.voucher_expenses > 0 ? (
              <Row label={t("accounting.project_summary.voucher_expenses")} value={formatAccountingCurrency(summary.voucher_expenses)} />
            ) : null}
            <div className="border-t pt-2 mt-2">
              <Row
                label={t("accounting.project_summary.total_costs")}
                value={formatAccountingCurrency(summary.total_costs)}
                valueClass="font-semibold text-gray-900"
              />
            </div>
          </div>
        </section>
      </div>

      <section className="mt-6 border border-gray-200 rounded-lg p-5 print:p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          {t("accounting.project_summary.print.result_section")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm print:grid-cols-3">
          <div>
            <p className="text-gray-500">{t("accounting.project_summary.profit")}</p>
            <p className={`text-xl font-bold ${summary.profit >= 0 ? "text-green-700" : "text-red-700"}`}>
              {formatAccountingCurrency(summary.profit)}
            </p>
          </div>
          <div>
            <p className="text-gray-500">{t("accounting.project_summary.margin")}</p>
            <p className={`text-xl font-bold ${summary.margin >= 0 ? "text-green-700" : "text-red-700"}`}>
              {formatAccountingPercentage(summary.margin)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {marginBasis === "contract"
                ? t("accounting.project_summary.print.margin_on_contract")
                : t("accounting.project_summary.print.margin_on_invoiced")}
            </p>
          </div>
          <div>
            <p className="text-gray-500">{t("accounting.project_summary.income_total")}</p>
            <p className="text-xl font-bold text-gray-900">
              {formatAccountingCurrency(income.total_invoiced_and_expected)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t("accounting.project_summary.print.minus_total_costs")}: {formatAccountingCurrency(summary.total_costs)}
            </p>
          </div>
        </div>
      </section>

      <MethodologyAppendix
        summary={summary}
        t={t}
        className={showMethodologyOnScreen ? "" : "hidden print:block"}
      />
    </>
  );
};

export const triggerProjectAccountingPdfDownload = async ({
  projectId,
  projectNumber,
  summary,
  locale,
  includeModules = true,
  endpoint,
  onError,
} = {}) => {
  if (!projectId) return;

  const year = summary?.last_updated
    ? new Date(summary.last_updated).getFullYear()
    : new Date().getFullYear();
  const number = projectNumber || summary?.project_number || projectId;
  const filename = `prosjektregnskap-${number}-${year}.pdf`;

  try {
    await downloadAccountingProjectSummaryPdf({
      projectId,
      year,
      locale,
      includeModules,
      filename,
      endpoint,
    });
  } catch (err) {
    console.error("Error downloading project accounting PDF:", err);
    const message =
      err.response?.data?.errors?.[0] ||
      err.message ||
      "Kunne ikke laste ned PDF";
    if (typeof onError === "function") {
      onError(message);
    } else {
      window.alert(message);
    }
  }
};

/** @deprecated browser print — use triggerProjectAccountingPdfDownload */
export const triggerProjectAccountingPrint = () => {
  window.print();
};
